// TESTIFY upload outbox (§13.8). Drains scanned results queued for upload:
// build the signed body → POST /upload → record the per-result outcome. One
// student per request (the endpoint's shape), one subject testItem — a GRADIFY
// quiz maps to a single TESTIFY subject, so each result is one testItem.
//
// Idempotent and resumable: only 'pending'/'rejected' rows are touched, each is
// flipped to 'synced' or back to 'rejected' with the server's reason, so a
// re-run retries exactly what still needs it. Errors are per-result — one bad
// row never blocks the rest.
import type { Repositories } from '../db/repositories.js';
import type { ResultRecord, QuizRecord, QuizAssignmentRecord, TestifyExamRecord, AnswerKeyRecord } from '../db/schema.js';
import { uploadResult, TestifyApiError } from './api.js';
import { deriveSigningKey, signSubject, signFile, type UploadResponse } from './signing.js';
import type { TestifyUploadBody } from './types.js';

export interface UploadOutcome {
  resultId: string;
  ok: boolean;
  status: 'synced' | 'rejected';
  /** why it was rejected (server message), for the review list. */
  error?: string;
  /** true when the whole batch should stop (auth/stale — re-import needed). */
  fatal?: boolean;
}

export interface DrainSummary {
  attempted: number;
  synced: number;
  rejected: number;
  skipped: number; // total rows that couldn't be built (sum of the two below)
  skippedNoLrn: number; // no confident 12-digit LRN — resolve the sheet first
  skippedNotMappable: number; // key has no upload map — re-import the exam
  outcomes: UploadOutcome[];
  /** set when a batch-fatal error stopped the drain early. */
  haltReason?: string;
}

type BuildResult = { body: TestifyUploadBody; lrn: string } | { skip: 'no-lrn' | 'not-mappable' };

/** The LRN a result carries: its matched student's, else the clean sheet read. */
async function lrnFor(repos: Repositories, r: ResultRecord): Promise<string | null> {
  if (r.studentId) {
    const s = await repos.getStudent(r.studentId);
    if (s && /^\d{12}$/.test(s.lrn)) return s.lrn;
  }
  return /^\d{12}$/.test(r.idDigitsRead) ? r.idDigitsRead : null;
}

/** Split a single roster "name" into last/first/middle, best-effort. The server
 *  re-scores by LRN, so names are for its record only; unsplittable names go to
 *  lastName. Handles "Last, First Middle" and "First Middle Last". */
function splitName(name?: string): { lastName?: string; firstName?: string; middleName?: string } {
  const n = (name ?? '').trim();
  if (!n) return {};
  if (n.includes(',')) {
    const [last, rest = ''] = n.split(',', 2).map((s) => s.trim());
    const parts = rest.split(/\s+/).filter(Boolean);
    return { lastName: last, firstName: parts[0], middleName: parts.slice(1).join(' ') || undefined };
  }
  const parts = n.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return { lastName: parts[0] };
  const lastName = parts[parts.length - 1];
  const firstName = parts[0];
  const middleName = parts.slice(1, -1).join(' ') || undefined;
  return { lastName, firstName, middleName };
}

/** Build the signed /upload body for one result, or a typed skip reason. */
async function buildBody(
  repos: Repositories,
  result: ResultRecord,
  quiz: QuizRecord,
  exam: TestifyExamRecord,
  key: AnswerKeyRecord,
): Promise<BuildResult> {
  // No upload map means the exam was imported before upload support existed —
  // re-importing the exam rebuilds the key with the quesId/choiceId map.
  if (!quiz.testId || !key.uploadMap) return { skip: 'not-mappable' };
  const lrn = await lrnFor(repos, result);
  if (!lrn) return { skip: 'no-lrn' }; // no confident 12-digit LRN — resolve the sheet first

  // Map each scanned item → { quesId, selectedChoiceId }. A blank or double-mark
  // (chosen null) or an option with no known choice_id becomes null.
  const responses: UploadResponse[] = [];
  for (const a of result.rawAnswers) {
    const m = key.uploadMap[a.q];
    if (!m) continue; // item not in the exam (shouldn't happen for a TESTIFY quiz)
    const selectedChoiceId = a.chosen ? (m.choiceIds[a.chosen] ?? null) : null;
    responses.push({ quesId: m.quesId, selectedChoiceId, timeSpentSeconds: 0, wasMarked: 0 });
  }

  const signingKey = await deriveSigningKey(exam.signingKey, exam.examCode, lrn, quiz.testId);
  const signature = await signSubject(signingKey, responses);
  const fileSignature = await signFile(signingKey, [signature]);
  const student = result.studentId ? await repos.getStudent(result.studentId) : undefined;

  const body: TestifyUploadBody = {
    exam_code: exam.examCode,
    compile_version: exam.compileVersionNum ?? Number(exam.compileVersion),
    student: { lrn, ...splitName(student?.name) },
    testItems: [{ testId: Number(quiz.testId), responses, signature }],
    fileSignature,
  };
  return { body, lrn };
}

/**
 * Drain the upload queue for one assignment. Returns a summary; per-result
 * statuses are persisted as it goes, so a caller can re-run to retry failures.
 * An `onProgress` callback fires after each result for live UI.
 */
export async function drainAssignment(
  repos: Repositories,
  assignment: QuizAssignmentRecord,
  onProgress?: (done: number, total: number, last: UploadOutcome) => void,
): Promise<DrainSummary> {
  const quiz = await repos.getQuiz(assignment.quizId);
  const summary: DrainSummary = { attempted: 0, synced: 0, rejected: 0, skipped: 0, skippedNoLrn: 0, skippedNotMappable: 0, outcomes: [] };
  if (!quiz || quiz.origin !== 'testify' || !quiz.testifyExamId) return summary;
  const exam = await repos.getTestifyExam(quiz.testifyExamId);
  if (!exam) return summary;
  const key = await repos.answerKeyFor(quiz.id);
  if (!key) return summary;

  const queue = await repos.uploadableResults(assignment.id);
  let done = 0;
  for (const result of queue) {
    const built = await buildBody(repos, result, quiz, exam, key);
    if ('skip' in built) {
      summary.skipped++;
      if (built.skip === 'no-lrn') summary.skippedNoLrn++;
      else summary.skippedNotMappable++;
      done++;
      continue;
    }
    summary.attempted++;
    try {
      await uploadResult(exam.token, built.body);
      await repos.setSyncStatus(result.id, 'synced', undefined);
      const outcome: UploadOutcome = { resultId: result.id, ok: true, status: 'synced' };
      summary.synced++;
      summary.outcomes.push(outcome);
      onProgress?.(++done, queue.length, outcome);
    } catch (e) {
      const err = e instanceof TestifyApiError ? e : new TestifyApiError(0, String(e));
      // 401 (token), 403 (exam/school mismatch) and 409 (stale compile_version)
      // hit every row the same way — stop the batch and surface one message.
      // 422 (bad LRN / tamper) is per-row: park it and keep going.
      const fatal = err.status === 401 || err.status === 403 || err.status === 409;
      await repos.setSyncStatus(result.id, 'rejected', err.message);
      const outcome: UploadOutcome = { resultId: result.id, ok: false, status: 'rejected', error: err.message, fatal };
      summary.rejected++;
      summary.outcomes.push(outcome);
      onProgress?.(++done, queue.length, outcome);
      if (fatal) {
        summary.haltReason = err.message;
        break;
      }
    }
  }
  return summary;
}
